/**
 * Largura arrastavel da coluna de conversas.
 *
 * Aceleres 2026-08-28: extraido de ChatList.vue (66 linhas). O bloco era
 * autocontido -- a unica dependencia externa e `visualToLayout`, e ela e
 * essencial: `clientX` anda em espaco visual, mas a largura vira um `width`
 * em CSS (layout). O dashboard do Chatwoot roda com zoom 0.8, entao sem a
 * conversao o divisor corre 1/0.8 = 1.25x mais depressa que o rato e foge
 * debaixo do cursor.
 *
 * O onUnmounted vai JUNTO de proposito: os listeners sao registrados em
 * `window`. Sem ele, arrastar uma vez e trocar de rota deixa o `mousemove`
 * preso e a coluna redimensiona sozinha.
 */
import { ref, onUnmounted } from 'vue';
import { visualToLayout } from 'dashboard/helper/dashboardZoom';

const STORAGE_KEY = 'acl_conv_list_width';
// Aceleres 2026-09-14 (facundo): 460 era forcado por `!important` no
// aceleres-overrides.css e travava o arrastar da pega; passa a ser so o valor
// inicial.
const DEFAULT_WIDTH = 460;
const MIN_WIDTH = 320;
const MAX_WIDTH = 900;

function readStoredWidth() {
  try {
    const raw = parseInt(localStorage.getItem(STORAGE_KEY) || '', 10);
    if (Number.isFinite(raw) && raw >= MIN_WIDTH && raw <= MAX_WIDTH) {
      return raw;
    }
  } catch (e) {
    /* ignore */
  }
  return DEFAULT_WIDTH;
}

function persist(width) {
  try {
    localStorage.setItem(STORAGE_KEY, String(width));
  } catch (e) {
    /* ignore */
  }
}

export function useAclListResize() {
  const aclListWidth = ref(readStoredWidth());
  const isAclResizing = ref(false);
  let dragStartX = 0;
  let dragStartW = 0;

  const onMove = e => {
    const dx = visualToLayout(e.clientX - dragStartX);
    let w = dragStartW + dx;
    if (w < MIN_WIDTH) w = MIN_WIDTH;
    if (w > MAX_WIDTH) w = MAX_WIDTH;
    aclListWidth.value = w;
  };

  const onEnd = () => {
    isAclResizing.value = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onEnd);
    persist(aclListWidth.value);
  };

  const onAclResizeStart = e => {
    isAclResizing.value = true;
    dragStartX = e.clientX;
    dragStartW = aclListWidth.value;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
  };

  const onAclResizeReset = () => {
    aclListWidth.value = DEFAULT_WIDTH;
    persist(DEFAULT_WIDTH);
  };

  onUnmounted(() => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onEnd);
  });

  return { aclListWidth, isAclResizing, onAclResizeStart, onAclResizeReset };
}
